///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Filled polygon draped on the spherical globe. Unlike PolygonGeometry
/// (boundary-only triangulation), this densifies the interior with a grid
/// of Steiner points before triangulating, so large polygons — a hurricane
/// forecast cone can span 20+ degrees of arc — conform to the earth's
/// curvature instead of chording beneath the surface. With ~0.5° interior
/// spacing the worst-case sag is a few hundred meters, well under the
/// altitude offsets used for surface overlays.
///

import { SweepContext } from "@/earthbrowser/libs/poly2tri"
import { wgs } from "@/earthbrowser/utils/geographic"
import { type cube } from "@stovokor/math/cube"
import { trig } from "@stovokor/math/math"
import { type sphere } from "@stovokor/math/sphere"
import { vec2 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

// Cap on boundary vertices after downsampling; feed cones arrive with
// 1000+ points (~0.02° spacing) which is far denser than rendering needs
const cMaxBoundaryPoints = 240

// Interior Steiner grid spacing in degrees: fine enough that triangle sag
// stays well under the draping altitude, coarse enough to keep the mesh small
const cMinSpacingDeg = 0.35
const cMaxSpacingDeg = 1.5
const cTargetGridCells = 28

export class DrapedPolygonGeometry implements Geometry {
	public position: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public transparency = true
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true

	/**
	 * @param ring - outer polygon ring as lon/lat degrees (closing duplicate optional)
	 * @param altitudeKm - drape altitude above the ellipsoid
	 * @throws when the ring is degenerate or triangulation fails; callers
	 *         should fall back to outline-only rendering
	 */
	constructor(context: StovokorContext, ring: vec2[], altitudeKm: number) {
		this.position = new VertexAttribute(context, 3)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position])

		const boundary = downsample(deduplicated(ring), cMaxBoundaryPoints)
		if (boundary.length < 3) {
			throw new Error("DrapedPolygonGeometry: degenerate ring")
		}

		const ctx = new SweepContext(boundary, { cloneArrays: true })
		for (const point of steinerGrid(boundary)) {
			ctx.addPoint(point)
		}
		ctx.triangulate()

		const toRad = trig.rad
		const sin = Math.sin
		const cos = Math.cos
		const elev = altitudeKm + wgs.a

		let index = 0
		for (const t of ctx.getTriangles()) {
			const points = t.getPoints()
			for (let i = 0; i < 3; i++) {
				const lon = points[i].x * toRad
				const lat = points[i].y * toRad
				const coslat = cos(lat) * elev
				this.position.addPos(sin(lon) * coslat, sin(lat) * elev, cos(lon) * coslat)
			}
			this.indexBuffer.triangle(index, index + 1, index + 2)
			index += 3
		}

		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

function deduplicated(points: vec2[]): vec2[] {
	const deduped: vec2[] = []
	let last: vec2 | null = null
	for (const point of points) {
		if (point.x !== last?.x || point.y !== last.y) {
			deduped.push(point)
			last = point
		}
	}
	if (deduped.length > 1) {
		const first = deduped[0]
		const tail = deduped[deduped.length - 1]
		if (first.x === tail.x && first.y === tail.y) {
			deduped.pop()
		}
	}
	return deduped
}

function downsample(points: vec2[], maxPoints: number): vec2[] {
	if (points.length <= maxPoints) return points
	const step = points.length / maxPoints
	const sampled: vec2[] = []
	for (let i = 0; i < points.length; i += step) {
		sampled.push(points[Math.floor(i)])
	}
	return deduplicated(sampled)
}

/**
 * Interior grid points strictly inside the ring. Each candidate must pass
 * the point-in-polygon test along with four inset neighbors, which keeps
 * Steiner points off the constrained boundary edges (poly2tri rejects
 * duplicate/on-edge points).
 */
function steinerGrid(ring: vec2[]): vec2[] {
	let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
	for (const p of ring) {
		if (p.x < minX) minX = p.x
		if (p.x > maxX) maxX = p.x
		if (p.y < minY) minY = p.y
		if (p.y > maxY) maxY = p.y
	}

	const maxDim = Math.max(maxX - minX, maxY - minY)
	const spacing = Math.min(cMaxSpacingDeg, Math.max(cMinSpacingDeg, maxDim / cTargetGridCells))
	const inset = spacing * 0.35

	const points: vec2[] = []
	for (let y = minY + spacing / 2; y < maxY; y += spacing) {
		for (let x = minX + spacing / 2; x < maxX; x += spacing) {
			if (
				pointInRing(x, y, ring) &&
				pointInRing(x - inset, y, ring) &&
				pointInRing(x + inset, y, ring) &&
				pointInRing(x, y - inset, ring) &&
				pointInRing(x, y + inset, ring)
			) {
				points.push(new vec2(x, y))
			}
		}
	}
	return points
}

function pointInRing(x: number, y: number, ring: vec2[]): boolean {
	let inside = false
	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		const xi = ring[i].x, yi = ring[i].y
		const xj = ring[j].x, yj = ring[j].y
		if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
			inside = !inside
		}
	}
	return inside
}
